import { newExternalUrl } from 'utils/urlHelpers';
import { v4 as uuidv4 } from 'uuid';
import { AuthError } from './error';

export class OAuthClient {
  private readonly env = {
    root: () => import.meta.env['VITE_GATEWAY_URL'] as string,
    realm: () => import.meta.env['VITE_KEYCLOAK_REALM'] as string,
    oauth: () => `${import.meta.env['VITE_GATEWAY_URL']}/oauth2` as const,
  };

  // Returns true for fully-qualified URLs; relative paths need the external URL wrapper
  private static isUrlOrPath(url: string) {
    return url.startsWith('http');
  }

  /**
   * URL that initiates Keycloak auto-login flow.
   * @returns The Keycloak auto-login URL.
   */
  private createLoginLink() {
    return `${this.env.root()}/auth/realms/${this.env.realm()}/login-link` as const;
  }

  async login(payload: {
    email: string;
    password: string;
    clientUUID: string;
    callbackUrl: string;
  }) {
    const res = await fetch(this.createLoginLink(), {
      method: 'post',
      mode: 'cors',
      // credentials: 'omit' avoids sending existing cookies, preventing CSRF on the login-link endpoint
      credentials: 'omit',
      body: JSON.stringify({
        username: payload.email,
        userUuid: payload.clientUUID,
        password: payload.password,
        clientId: 'oauth2-proxy',
        redirectUrl: this.createAuthUrl(payload.callbackUrl),
        nonce: uuidv4(), // Random nonce prevents replay attacks on the login flow
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new AuthError({ type: 'no-access' });
      }
      throw new AuthError({ type: 'unknown-error', error: res });
    }

    const json = await res.json();
    return json;
  }

  /**
   * Creates an OAuth sign-in URL.
   *
   * The resulting URL will contain the `rd` query parameter which the backend
   * uses to redirect the user after a successful login.
   *
   * @param redirect - The path to redirect the user to after a successful login like redirect = '/login/confirm' or redirect = 'https://www.example.com'.
   * @returns The OAuth sign-in URL.
   */
  createAuthUrl(redirect: string) {
    const redirectUrl = OAuthClient.isUrlOrPath(redirect)
      ? redirect
      : newExternalUrl(redirect);

    const url = new URL(`${this.env.oauth()}/sign_in`);
    url.searchParams.set('rd', redirectUrl);

    return url.toString();
  }

  /**
   * Creates an OAuth sign-out URL.
   *
   * @param redirect - The path to redirect the user to after a successful logout like redirect = '/login' or redirect = 'https://www.example.com'.
   * @returns The OAuth sign-out URL.
   */
  createLogoutUrl(redirect: string) {
    const redirectUrl = OAuthClient.isUrlOrPath(redirect)
      ? redirect
      : newExternalUrl(redirect);

    const url = new URL(`${this.env.oauth()}/sign_out`);
    url.searchParams.set('rd', redirectUrl);

    return url.toString();
  }
}
